import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

export const APPOINTMENT_DURATION_OPTIONS = [15, 20, 30, 45, 60, 90, 120] as const;

export const DEFAULT_APPOINTMENT_DURATION_MIN = 20;

export const PERIOD_LABELS: Record<string, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

export function formatSlotDisplay(time: string, timezone?: string): string {
  if (!time) return '';
  if (timezone) {
    return formatInTimeZone(time, timezone, 'h:mm a');
  }
  const match = time.match(/(\d{2}):(\d{2})/);
  if (!match) return time;
  const h = Number(match[1]);
  const m = Number(match[2]);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
}

export function getSlotPeriod(time: string, timezone?: string): 'morning' | 'afternoon' | 'evening' {
  let h: number;
  if (timezone) {
    h = Number(formatInTimeZone(time, timezone, 'H'));
  } else {
    const match = time.match(/(\d{2}):(\d{2})/);
    h = match ? Number(match[1]) : 0;
  }
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

/** Local wall-clock `HH:mm` for an instant in the clinic timezone. */
export function isoToTimeInputValue(iso: string, timezone: string): string {
  try {
    return formatInTimeZone(iso, timezone, 'HH:mm');
  } catch {
    return '';
  }
}

/** Build an ISO instant from a calendar date + `HH:mm` in the clinic timezone. */
export function dateAndTimeToIso(dateYmd: string, timeHm: string, timezone: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateYmd) || !/^\d{2}:\d{2}$/.test(timeHm)) return null;
  try {
    const utc = fromZonedTime(`${dateYmd}T${timeHm}:00`, timezone);
    return utc.toISOString();
  } catch {
    return null;
  }
}

export function addMinutesToIso(iso: string, minutes: number): string {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() + minutes);
  return d.toISOString();
}

export function minutesBetweenIso(startIso: string, endIso: string): number {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.round((end - start) / 60_000);
}

/** Snap duration to the closest allowed option when possible; otherwise clamp 5–240. */
export function normalizeDurationMinutes(minutes: number): number {
  if (!Number.isFinite(minutes) || minutes < 5) return DEFAULT_APPOINTMENT_DURATION_MIN;
  const clamped = Math.min(240, Math.max(5, Math.round(minutes)));
  const exact = APPOINTMENT_DURATION_OPTIONS.find((d) => d === clamped);
  if (exact !== undefined) return exact;
  return clamped;
}

/**
 * Resolves wall-clock query-param prefill into start/end ISO instants.
 * Returns null when date/time are invalid for the given timezone.
 */
export function resolvePrefillSlotIso(
  dateYmd: string,
  timeHm: string,
  timezone: string,
  durationMinutes: number,
): { startAt: string; endAt: string } | null {
  const startAt = dateAndTimeToIso(dateYmd, timeHm, timezone);
  if (!startAt) return null;
  return {
    startAt,
    endAt: addMinutesToIso(startAt, durationMinutes),
  };
}
